// THE WASM LOADER (R8c1): a client wasm artefact (vrm_core/dist/<name>.wasm,
// copied to dist/wasm/<name>.wasm by scripts/build-client.js copyWasm) is
// fetched and compiled ONCE per page, after the quiet start (heavyReady: MAP
// FIRST — no wasm byte before), in the asset queue's bulk lane (the first
// caller may name another class: wasmCore('vrm_core') asks `near`). The compiled
// WebAssembly.Module is structured-clonable: client/src/pool.js posts it to
// each worker that runs a job naming it (run(op, args, { wasm: name })), and
// the worker instantiates it there (poolwork.js) — one download, one compile.
//
// The urls come from the build define __VRM_WASM__ = { name:
// 'wasm/<name>.wasm?v=<sha16>' } (relative, like `materials/…?v=`: gs.js
// serves a `v` that names the content immutable, client/sw.js keeps it
// cache-first). Two ship (build-client.js CLIENT_WASM): vrm_core (below) and
// vrm_ways (vrm_core/ways/vrm_ways.mjs wraps the worker's instance exports:
// new Ways({ exports })) — no consumer asks for vrm_ways yet (the dp consumers
// run before heavyReady — R8h), so no page fetches it.
//
// THE MAIN THREAD (rearch-r8k): wasmCore(name) instantiates the module here
// too, wrapped in the vrm_core glue (vrm_core/js/vrm_core.mjs) — the client's
// one ground (ground.js) samples the GM03 nodes the game server samples,
// through the same artefact. Its first caller may raise the fetch to `near`
// (the ground is what the beam-in latch waits on, never a bulk download).
//
// fetchAsset (assetq.js) resolves a body, not a Response, so this is
// fetchAsset(…, 'arrayBuffer') + WebAssembly.compile rather than
// compileStreaming: the queue's lane, pause reasons, retries and accounting
// over the streaming overlap (async compile is off the main thread either way).
import { fetchAsset } from './assetq.js';
import { heavyReady } from './sched.js';
import { instantiate } from '../../vrm_core/js/vrm_core.mjs';

/* global __VRM_WASM__ */
let _urls = typeof __VRM_WASM__ !== 'undefined' ? __VRM_WASM__ : {};
const _mods = new Map();          // name -> Promise<WebAssembly.Module> (memoised, a failure too: loud, never refetched)
const _cores = new Map();         // name -> Promise<the glue's Core> (main thread, once)

// the gates' hook: the url table (a build has the define), the memo cleared
export function configureWasm({ urls } = {}) {
  if (urls && typeof urls === 'object') _urls = urls;
  _mods.clear();
  _cores.clear();
}
export const wasmUrls = () => ({ ..._urls });

// name -> Promise of its compiled WebAssembly.Module (once per artefact);
// prio: the asset queue class of its one fetch (the first caller's)
export function wasmModule(name, { prio = 'bulk' } = {}) {
  let p = _mods.get(name);
  if (p) return p;
  p = (async () => {
    const url = Object.prototype.hasOwnProperty.call(_urls, name) ? _urls[name] : null;
    if (!url) throw new Error(`wasm: no artefact ${name} in this build (__VRM_WASM__)`);
    await heavyReady();
    const bytes = await fetchAsset(url, { prio, as: 'arrayBuffer' });
    if (!bytes) throw new Error(`wasm: ${url} not ok`);
    return WebAssembly.compile(bytes);
  })();
  _mods.set(name, p);
  return p;
}

// name -> Promise of the vrm_core glue's Core over a main-thread instance
export function wasmCore(name, opts = {}) {
  let p = _cores.get(name);
  if (!p) { p = wasmModule(name, opts).then((m) => instantiate(m)); _cores.set(name, p); }
  return p;
}
